import EzController from 'controllers/ez-base-controller';
import Iro from 'lib/vendor/iro';
import {
  triggerCustomEvent,
  getWindowWidth,
  splitTestEnabled,
  getContentType,
} from 'lib/helpers';
import events from 'lib/events';
import { FILTERS_APPLIED_CLICK } from 'components/analytics/conversions/constants/actions';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import SearchStateManager from 'lib/search-state';

const searchState = new SearchStateManager();

export default class ColorFilterController extends EzController {
  static targets = [
    'activeTrack',
    'hexText',
    'colorSwatch',
    'slash',
    'rangeA',
  ]

  filterEvents = [];

  vectorProps = {
    min: 10,
    max: 40,
    default: 14,
  }

  photoProps = {
    min: 20,
    max: 50,
    default: 24,
  }

  connect() {
    this.setupColorWheel();
    this.setupThreshold();

    this.colorWheel.on('mount', this.setPickerBkg);
    this.colorWheel.on('input:end', this.colorChangeCallback);

    window.addEventListener('resize', this.setColorWheelWidth);
    window.addEventListener('resetColorSwatchFilter', this.colorReset);

    this.hexTextTarget.addEventListener('change', this.hexTextChange);

    this.flagLoaded();
  }

  disconnect() {
    window.removeEventListener('resize', this.setColorWheelWidth);
    window.removeEventListener('resetColorSwatchFilter', this.colorReset);

    this.colorWheel.off('mount', this.setPickerBkg);
    this.colorWheel.off('input:end', this.colorChangeCallback);
    this.hexTextTarget.removeEventListener('change', this.hexTextChange);
  }

  hexTextChange = () => {
    let newColor = this.hexTextTarget.value.replace(/^#/, '');
    const lastColor = this.colorSwatchTarget.dataset.lastColor ? this.colorSwatchTarget.dataset.lastColor : null;

    switch (newColor.length) {
      case 1: newColor = newColor.repeat(6); break;
      case 2: newColor = newColor.repeat(3); break;
      case 3: newColor = newColor.repeat(2); break;
      case 4: newColor += newColor.substring(0, 2); break;
      case 5: newColor += newColor.substring(0, 1); break;
      default: newColor; /* eslint-disable-line no-unused-expressions */
    }

    if (this.isValidHex(newColor)) {
      this.colorWheel.color.hexString = `#${newColor}`;
      this.hexTextTarget.value = `#${newColor}`;
      this.colorChangeCallback(newColor);
    } else if (lastColor) {
      this.colorWheel.color.hexString = lastColor;
      this.hexTextTarget.value = lastColor;
    } else {
      this.colorReset();
    }
  }

  setupColorWheel() {
    const state = searchState.getRawStateObject();
    const color = state.color ? state.color : '#d4d4d4';

    this.colorWheel = new Iro.ColorPicker('.color-filter-options__color-wheel', {
      width: this.getColorWheelWidth(),
      display: 'block',
      sliderMargin: 16,
      wheelAngle: 60,
      padding: 3,
      color,
    });
  }

  setPickerBkg() {
    document.querySelector('.IroWheel .IroHandle').children[1].style.fillOpacity = '0';
  }

  getColorWheelWidth() {
    return getWindowWidth() > 750 ? 208 : 269;
  }

  colorChangeCallback = (color) => {
    const hexValue = color.hexString ? color.hexString.replace(/#(?=\S)/g, '') : color.replace(/#(?=\S)/g, '');

    this.addSwatchToEzSearchForm(hexValue);
    this.hexTextTarget.closest('.ez-input--animated-label').classList.add('is-focused');
    this.hexTextTarget.value = `#${hexValue}`;
    this.element.dataset.lastColor = hexValue;
    this.colorSwatchTarget.style.backgroundColor = `#${hexValue}`;
    this.colorSwatchTarget.classList.add('color-filter-options__swatch--active');

    if (searchState.getRawStateObject().threshold === '') {
      this.setFilter('threshold', `threshold-${this.getDefaultThreshold()}`, 'update', false);
    }

    this.setFilter('color', `color-${hexValue}`);
  }

  setColorWheelWidth = () => {
    this.colorWheel.setState({ width: this.getColorWheelWidth() });
  }

  setupThreshold = () => {
    const state = searchState.getRawStateObject();
    const vector = this.vectorProps;
    const photo = this.photoProps;
    let threshold = state.threshold ? state.threshold : this.getDefaultThreshold();

    if (getContentType() === 'vector') {
      threshold = this.calculateThreshold(threshold, vector.min, vector.max);
    } else {
      threshold = this.calculateThreshold(threshold, photo.min, photo.max);
    }

    this.rangeATarget.value = threshold;
    this.activeTrackTarget.style.width = `${threshold}%`;
    this.updateColorWheelPicker(threshold);
  }

  calculateThreshold(value, min, max) {
    return Math.round(((value - min) / (max - min)) * 100);
  }

  getDefaultThreshold() {
    return getContentType() === 'vector' ? this.vectorProps.default : this.photoProps.default;
  }

  handleSlider = (e = false) => {
    this.activeTrackTarget.style.width = `${e.target.value}%`;
    this.updateColorWheelPicker(e.target.value);
  }

  updateColorWheelPicker = (threshold) => {
    const colorWheelPickerRadius = 15 + ((23 / 100) * threshold);
    const colorWheelMargin = 16 + ((19 / 100) * threshold);

    this.colorWheel.setState({
      sliderMargin: colorWheelMargin,
      activeHandleRadius: colorWheelPickerRadius,
    });
  }

  setThreshold = (e = false) => {
    const vector = this.vectorProps;
    const photo = this.photoProps;
    let thresholdOffset;

    if (getContentType() === 'vector') {
      thresholdOffset = this.calculateThresholdOffset(e.target.value, vector.min, vector.max, true);
    } else {
      thresholdOffset = this.calculateThresholdOffset(e.target.value, photo.min, photo.max, true);
    }

    this.setFilter('threshold', `threshold-${thresholdOffset}`);
  }

  calculateThresholdOffset(value, min, max, round = false) {
    return round ? Math.round(min + (((max - min) / 100) * value)) : min + (((max - min) / 100) * value);
  }

  colorReset = () => {
    const state = searchState.getRawStateObject();
    const { lastColor } = this.colorSwatchTarget.dataset;
    this.colorWheel.reset();

    this.hexTextTarget.value = '';
    this.hexTextTarget.closest('.ez-input--animated-label').classList.remove('is-focused');
    this.colorSwatchTarget.style.backgroundColor = '#ffffff';
    this.colorSwatchTarget.classList.remove('color-filter-options__swatch--active');

    if (lastColor) this.setFilter('color', `color-${lastColor}`, 'remove');
    if (state.threshold) this.setFilter('threshold', `threshold-${state.threshold}`, 'remove');
  }

  isValidHex = (color) => {
    if (!color || typeof color !== 'string') return false;

    switch (color.length) {
      case 6: return /^[0-9A-F]{6}$/i.test(color);
      default: return false;
    }
  }

  setFilter = (filterType = 'color', hexValue = false, action = 'update', sendTracking = true) => {
    if (action === 'remove') {
      searchState.removeParamKey(hexValue);
    } else {
      searchState.updateParamKey(hexValue);

      if (sendTracking) {
        const filterEvent = {
          action: FILTERS_APPLIED_CLICK,
          label: `Filter-${filterType}`,
          filter_selection: `Filter-${hexValue}`,
        };

        this.filterEvents.push(filterEvent);
        sendTrackingEvent({ data: filterEvent });
      }
    }

    if (splitTestEnabled('remove-mobile-filters-apply')) {
      this.applyFilters();
    } else if (getWindowWidth() >= 750) this.applyFilters();
  };

  applyFilters() {
    if (getWindowWidth() < 750 && splitTestEnabled('remove-mobile-filters-apply')) {
      this.setSearchFiltersHiddenKey('mobile-filter-panel', true);
    }

    triggerCustomEvent(events.search.apply_filters, {
      contentType: getContentType(),
    });

    if (getWindowWidth() < 750) {
      if (!splitTestEnabled('remove-mobile-filters-apply')) {
        this.hideSearchFilters();
      }
    }
  }

  addSwatchToEzSearchForm = (hexValue) => {
    triggerCustomEvent('updateEzSearchFormColorSwatch', {
      color: hexValue,
    });
  }
}
